import type { Doc } from "building-types/doc";
import type { Table } from "common/data/table";
import type {
  DocAssociationSourceEntityType,
  DocAssociationTargetEntityType,
  DocEntityAssociation,
  DocAssociationTargetTypeToSourceType,
  DocAssociationSourceTypeToTargetType,
} from "./associationTypes";

/**
 * Extracts the keys of a table that contain DocEntityAssociations.
 */
export type AssociationTableFieldNames<T> = {
  [K in keyof T]: T[K] extends Table<DocEntityAssociation> ? K : never;
}[keyof T];

export type DocAssociationTableFieldNames = Exclude<AssociationTableFieldNames<Doc>, undefined>;

/**
 * A table that maps source entity types to their corresponding doc tables.
 */

const docFacadeAssociationTables: {
  readonly [key in DocAssociationSourceTypeToTargetType["FACADE"]]: DocAssociationTableFieldNames;
} = {
  FACADE_SPAN_STRATUM: "facadeSpanStratumAssociations",
  FACADE_SWEEP_STRATUM: "facadeSweepStratumAssociations",
};

const docDatumAssociationTables: {
  readonly [key in DocAssociationSourceTypeToTargetType["DATUM"]]: DocAssociationTableFieldNames;
} = {
  FACADE_SPAN_STRATUM: "datumSpanAssociations",
};

export const docSourceEntityTypeToAssociationTables: {
  readonly [key in DocAssociationSourceEntityType]: {
    [key in DocAssociationTargetEntityType]?: DocAssociationTableFieldNames;
  };
} = {
  FACADE: docFacadeAssociationTables,
  DATUM: docDatumAssociationTables,
};

/**
 * A table that maps target entity types to their corresponding doc tables.
 */

const docFacadeSpanStratumAssociationTables: {
  readonly [key in DocAssociationTargetTypeToSourceType["FACADE_SPAN_STRATUM"]]: DocAssociationTableFieldNames;
} = {
  FACADE: "facadeSpanStratumAssociations",
  DATUM: "datumSpanAssociations",
};

const docFacadeSweepStratumAssociationTables: {
  readonly [key in DocAssociationTargetTypeToSourceType["FACADE_SWEEP_STRATUM"]]: DocAssociationTableFieldNames;
} = {
  FACADE: "facadeSweepStratumAssociations",
};

export const docTargetEntityTypeToAssociationTables: {
  readonly [key in DocAssociationTargetEntityType]: {
    [key in DocAssociationSourceEntityType]?: DocAssociationTableFieldNames;
  };
} = {
  FACADE_SPAN_STRATUM: docFacadeSpanStratumAssociationTables,
  FACADE_SWEEP_STRATUM: docFacadeSweepStratumAssociationTables,
};

/**
 * All association tables for the doc.
 */
export const docFieldIsAssociationTable: { readonly [key in DocAssociationTableFieldNames]: true } = {
  facadeSpanStratumAssociations: true,
  facadeSweepStratumAssociations: true,
  datumSpanAssociations: true,
};
